import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { useProjectStore } from '@/store/projectStore';
import { toast } from '@/store/toastStore';
import { canCopyGif, copyGifToClipboard } from '@/utils/clipboard';
import { formatBytes, formatTimecode } from '@/utils/format';
import { buildOutputName } from '@/utils/sanitize';

export function ResultCard() {
  const result = useProjectStore((state) => state.result);
  const sourceName = useProjectStore((state) => state.meta?.name ?? 'clip');
  const dismissResult = useProjectStore((state) => state.dismissResult);
  const gifUrl = useObjectUrl(result?.blob ?? null);
  const [copied, setCopied] = useState(false);

  if (!result || !gifUrl) return null;

  const fileName = buildOutputName(sourceName, result.width, result.height, result.fps);

  const handleDownload = () => {
    const anchor = document.createElement('a');
    anchor.href = gifUrl;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  };

  const handleCopy = async () => {
    try {
      await copyGifToClipboard(result.blob);
      setCopied(true);
      toast.success('Copied', 'The GIF is on your clipboard.');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy", 'Your browser blocked the clipboard — download instead.');
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      aria-label="Finished GIF"
      className="panel space-y-3 p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="eyebrow">Your GIF</h3>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Dismiss result"
          onClick={dismissResult}
          className="px-2"
        >
          <X aria-hidden className="size-4" />
        </Button>
      </div>

      <div className="checker grid place-items-center rounded-lg p-3 ring-1 ring-ink-200 dark:ring-ink-700">
        {/* The encoded artifact itself — the truthful preview. */}
        <img
          src={gifUrl}
          alt={`Finished GIF, ${result.width} by ${result.height} pixels`}
          className="max-h-80 max-w-full rounded"
          width={result.width}
          height={result.height}
        />
      </div>

      <p className="font-mono text-xs text-ink-500 tabular-nums dark:text-ink-400">
        {result.width}×{result.height} · {result.fps} fps · {formatTimecode(result.durationSec)} ·{' '}
        <span className="text-ink-800 dark:text-ink-100">{formatBytes(result.size)}</span>
      </p>

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={handleDownload}>
          <Download aria-hidden className="size-4" />
          Download {fileName}
        </Button>
        {canCopyGif() && (
          <Button variant="secondary" onClick={() => void handleCopy()}>
            {copied ? (
              <Check aria-hidden className="size-4 text-good-500" />
            ) : (
              <Copy aria-hidden className="size-4" />
            )}
            {copied ? 'Copied' : 'Copy GIF'}
          </Button>
        )}
      </div>
    </motion.section>
  );
}
